import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { DownloadTheApp, TrustpilotReviews } from 'wanderu-app-gettheapp';
import {
    Control,
    SplitTest,
    Variant,
} from 'wanderu-component-split-test';
import {
    BackToTopButton,
    FAQSection,
    LongFormContent,
    Section,
    ValueProps,
    ValueStatement,
} from 'wanderu-components';
import { C2C_NAV_ITEMS } from 'wanderu-ducks-seo';
import { localize } from 'wanderu-i18n';
import {
    isSessionStorageAvailable,
} from 'wanderu-utils';

import cityToCityConfig from '../../../config/c2c.locale';
import { isTranslated } from '../../utils';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import CarrierLinkSection from '../CarrierLinkSection/CarrierLinkSection';
import SimplifiedCarrierReviews from '../CarrierReviews/SimplifiedCarrierReviews';
import CarrierSection from '../CarrierSection';
import EditorialBlurbs from '../EditorialBlurbs/EditorialBlurbs';
import GeneralSection from '../GeneralSection/GeneralSection';
import InfoSection from '../InfoSection/InfoSection';
import LinkSection from '../LinkSection/LinkSection';
import LinkTable from '../LinkTable/LinkTable';
import PriceChart from '../PriceChart/PriceChart';
import PromoBox from '../PromoBox/PromoBox';
import TableOfContentsSection from '../TableOfContents/TableOfContents';
import AlternativeMode from './AlternativeMode/AlternativeMode';
import CityToCityHeader from './CityToCityHeader/CityToCityHeader';
import JourneyInfo from './JourneyInfo';
import NewsletterSubscription from './NewsletterSubscription/NewsletterSubscription';
import ReturnTripCTA from './ReturnTripCTA/ReturnTripCTA';
import RouteMap from './RouteMap/RouteMap';
import ScheduleContainer from './ScheduleContainer/ScheduleContainer';
import TravelStats from './TravelStats/TravelStats';
import UpcomingTrips from './UpcomingTrips/UpcomingTrips';

function splitShortTimeString(timeString) {
    return timeString.match(/([^a-z]+)(.*)/);
}

function generateNavItemsList({
    showCarrierSectionNav,
    showCityBlurbNav,
    showFaqNav,
    showOverviewNav,
    showPriceSectionNav,
    showRelatedReadingsNav,
    showReviewNav,
    showScheduleNav,
    showStationBlurbNav,
}) {
    const navItems = [];

    if (showScheduleNav) {
        navItems.push(C2C_NAV_ITEMS.scheduleNav);
    }

    if (showOverviewNav) {
        navItems.push(C2C_NAV_ITEMS.overviewNav);
    }

    if (showReviewNav) {
        navItems.push(C2C_NAV_ITEMS.reviewNav);
    }

    if (showPriceSectionNav) {
        navItems.push(C2C_NAV_ITEMS.priceSectionNav);
    }

    if (showCarrierSectionNav) {
        navItems.push(C2C_NAV_ITEMS.carrierSectionNav);
    }

    if (showFaqNav) {
        navItems.push(C2C_NAV_ITEMS.faqNav);
    }

    if (showCityBlurbNav) {
        navItems.push(C2C_NAV_ITEMS.cityBlurbNav);
    }

    if (showStationBlurbNav) {
        navItems.push(C2C_NAV_ITEMS.stationBlurbNav);
    }

    if (showRelatedReadingsNav) {
        navItems.push(C2C_NAV_ITEMS.relatedReadingsNav);
    }

    return navItems;
}

class CityToCity extends Component {
    componentDidMount() {
        const {
            transMode,
        } = this.props;

        if (!isSessionStorageAvailable()) {
            return;
        }

        window.sessionStorage.setItem('seo_referrer', transMode);
    }

    render() {
        const {
            altTransModeStats,
            blurbSectionContent = {},
            breadcrumbData,
            c2cRevampVariant,
            carrierLinks,
            cheapestTicketData,
            cityContent,
            cityToCityCarrierData,
            content,
            dailyTripsData,
            distanceData,
            history,
            i18nMoment,
            i18nMoney,
            infoSectionContent,
            interlinkData,
            isFetchingAllTrips,
            isFetchingCachedTrips,
            journeyInfo,
            language,
            locale,
            locationData,
            longFormContent,
            onTimeTableDateSelect,
            priceHistory,
            renderFlags,
            reviewSummary,
            routeMapImageSlug,
            scheduleData,
            simplifiedCarrierReviews,
            t,
            timeTableDate,
            transMode,
            travelFrequencyData,
            upcomingTrips,
        } = this.props;
        const {
            busiestDayOfWeek,
            quietestDayOfWeek,
        } = travelFrequencyData;
        const {
            km: distanceInKm,
            mi: distanceInMiles,
        } = distanceData;
        const {
            cheapestAvgPrice,
            savingsPriceUsd,
        } = cheapestTicketData;
        const {
            avgDailyDirectTrips,
            avgDailyTrips,
            earliestDeparture,
            latestDeparture,
            otherTransModePricing,
        } = dailyTripsData;
        const {
            arriveStations = [],
            departStations = [],
        } = blurbSectionContent;
        const {
            showCityBlurbArriveSection,
            showCityBlurbDepartSection,
            showStationBlurbArriveSection,
            showStationBlurbDepartSection,
        } = renderFlags;
        // Temporarily hidden while blog posts migrate from WordPress to nexus
        // (nexus requires post tagging that is not yet in place). This forces
        // the blog section and its "related readings" nav item off regardless
        // of renderFlags.showBlogBlurbSection. Re-enable by restoring the
        // renderFlags destructure. See wanderu/ui-react#1070.
        const showBlogBlurbSection = false;
        const {
            moovitDescription,
            moovitTitle,
        } = content;
        const {
            intro,
            sections,
            shortcuts,
        } = longFormContent;

        const dailyTripsDescriptionData = {
            minPriceUsd1: otherTransModePricing.price1Usd
                ? i18nMoney(otherTransModePricing.price1Usd).format()
                : undefined,
            minPriceUsd2: otherTransModePricing.price2Usd
                ? i18nMoney(otherTransModePricing.price2Usd).format()
                : undefined,
            minPriceUsdReverseLink: otherTransModePricing.priceReverseUsd
                ? i18nMoney(otherTransModePricing.priceReverseUsd).format()
                : undefined,
        };

        // split up timestamps into two parts (if used by current locale)
        const earliestDepartureString = i18nMoment(earliestDeparture).toShortTime(true);
        const earliestDepartureHour = splitShortTimeString(earliestDepartureString);

        const latestDepartureString = i18nMoment(latestDeparture).toShortTime(true);
        const latestDepartureHour = splitShortTimeString(latestDepartureString);

        const earliestLatestDescriptionData = {
            earliestDepartureTime: earliestDepartureString,
            latestDepartureTime: latestDepartureString,
        };

        const hasInterlinks = !!interlinkData.depart?.length && !!interlinkData.arrive?.length;
        const showUpcomingTrips = !!timeTableDate || !!scheduleData.length;
        const showPriceSection = !!Object.keys(priceHistory).length;
        const showCarrierSection = !!cityToCityCarrierData.length;
        const showSimplifiedReviews = !!simplifiedCarrierReviews.length;
        const showCityBlurb = showCityBlurbDepartSection || showCityBlurbArriveSection;
        const showStationBlurb = showStationBlurbDepartSection || showStationBlurbArriveSection;

        const navItems = generateNavItemsList({
            showCarrierSectionNav: showCarrierSection,
            showCityBlurbNav: c2cRevampVariant === 'control' && showCityBlurb,
            showFaqNav: true,
            showOverviewNav: true,
            showPriceSectionNav: showPriceSection,
            showRelatedReadingsNav: c2cRevampVariant === 'control' && showBlogBlurbSection,
            showReviewNav: showSimplifiedReviews,
            showScheduleNav: showUpcomingTrips,
            showStationBlurbNav: showStationBlurb,
        });

        return (
            <main>
                <CityToCityHeader
                    departDate={timeTableDate}
                    locationData={locationData}
                />
                <SplitTest testName="adtDiscount">
                    <Control />
                    <Variant name="group_a">
                        <PromoBox locale={locale} />
                    </Variant>
                </SplitTest>
                <TableOfContentsSection
                    namespace={cityToCityConfig.namespace}
                    navItems={navItems}
                />
                <Section>
                    <ValueProps />
                </Section>
                <TrustpilotReviews locale={locale} />
                <DownloadTheApp campaign="web-city_to_city" />
                <SplitTest testName="c2cRevamp">
                    <Control>
                        <GeneralSection
                            description={t('travelTipsDescription')}
                            id="travel-tips"
                            show={isTranslated(t, ['travelTipsTitle', 'travelTipsDescription'])}
                            title={t('travelTipsTitle')}
                        />
                    </Control>
                    <Variant name="group_a" />
                </SplitTest>
                {showUpcomingTrips && (
                    <SplitTest testName="UpcomingSchedulesV3">
                        <Control>
                            <UpcomingTrips
                                departDate={timeTableDate}
                                isLoading={isFetchingCachedTrips || isFetchingAllTrips}
                                language={language}
                                locale={locale}
                                locationData={locationData}
                                namespace={cityToCityConfig.namespace}
                                onDateSelect={onTimeTableDateSelect}
                                transMode={transMode}
                                trips={upcomingTrips}
                            />
                        </Control>
                        <Variant name="schedules">
                            <ScheduleContainer
                                departDate={timeTableDate}
                                isLoading={isFetchingCachedTrips || isFetchingAllTrips}
                                language={language}
                                locale={locale}
                                locationData={locationData}
                                namespace={cityToCityConfig.namespace}
                                onDateSelect={onTimeTableDateSelect}
                                schedule={scheduleData}
                                transMode={transMode}
                            />
                        </Variant>
                    </SplitTest>
                )}
                <SplitTest testName="c2cRevamp">
                    <Control>
                        {(altTransModeStats?.cheapest || altTransModeStats?.fastest) && (
                            <AlternativeMode
                                data={altTransModeStats}
                                departDate={timeTableDate}
                                history={history}
                                locale={locale}
                                locationData={locationData}
                            />
                        )}
                    </Control>
                    <Variant name="group_a" />
                </SplitTest>
                <JourneyInfo
                    distanceInKm={distanceInKm}
                    distanceInMiles={distanceInMiles}
                    journeyInfo={journeyInfo}
                    namespace={cityToCityConfig.namespace}
                    transMode={transMode}
                />
                <NewsletterSubscription
                    destinationCity={locationData?.arrival?.name}
                    locale={locale}
                    mode={transMode}
                    originCity={locationData?.departure?.name}
                />
                <SplitTest testName="c2cRevamp">
                    <Control>
                        {showSimplifiedReviews && (
                            <SimplifiedCarrierReviews
                                carrierReviews={simplifiedCarrierReviews}
                                namespace={cityToCityConfig.namespace}
                                reviewSummary={reviewSummary}
                            />
                        )}
                    </Control>
                    <Variant name="group_a" />
                </SplitTest>
                {routeMapImageSlug && (
                    <RouteMap
                        locationData={locationData}
                        slug={routeMapImageSlug}
                    />
                )}
                <SplitTest testName="c2cRevamp">
                    <Control>
                        <CarrierLinkSection
                            links={carrierLinks}
                            namespace={cityToCityConfig.namespace}
                            showAnchor={showCarrierSection}
                            transMode={transMode}
                        />
                        {showPriceSection && (
                            <PriceChart
                                cheapestPrice={cheapestAvgPrice}
                                namespace={cityToCityConfig.namespace}
                                priceHistory={priceHistory}
                                savingsPriceUsd={savingsPriceUsd}
                            />
                        )}
                        {showCarrierSection && (
                            <CarrierSection
                                data={cityToCityCarrierData}
                                description={t('carriersDescription')}
                                namespace={cityToCityConfig.namespace}
                                title={t('carriersTitle')}
                            />
                        )}
                    </Control>
                    <Variant name="group_a">
                        <EditorialBlurbs
                            arrivalStationsSubtitle={t('popularStationsArrivalOtherSubtitle')}
                            blurbContent={{
                                ...blurbSectionContent,
                                arriveStationImages: [],
                                departStationImages: [],
                                moovitDescription,
                                moovitTitle,
                            }}
                            departureStationsSubtitle={t('popularStationsDepartureOtherSubtitle')}
                            label="station-editorial-blurb"
                            namespace={cityToCityConfig.namespace}
                            showArriveSection={showStationBlurbArriveSection}
                            showDepartSection={showStationBlurbDepartSection}
                            title={t('popularStationsTitle')}
                            variant="stations"
                        />
                        {showCarrierSection && (
                            <CarrierSection
                                data={cityToCityCarrierData}
                                description={t('carriersDescription')}
                                namespace={cityToCityConfig.namespace}
                                title={t('carriersTitle')}
                            />
                        )}
                        {showSimplifiedReviews && (
                            <SimplifiedCarrierReviews
                                carrierReviews={simplifiedCarrierReviews}
                                namespace={cityToCityConfig.namespace}
                                reviewSummary={reviewSummary}
                            />
                        )}
                        {showPriceSection && (
                            <PriceChart
                                cheapestPrice={cheapestAvgPrice}
                                namespace={cityToCityConfig.namespace}
                                priceHistory={priceHistory}
                                savingsPriceUsd={savingsPriceUsd}
                            />
                        )}
                    </Variant>
                </SplitTest>
                <FAQSection title={t('faqHeader')}>
                    {infoSectionContent.map((info) => (
                        <InfoSection
                            id={info.prefix}
                            infoText={info.text}
                            infoTitle={info.title}
                            key={info.prefix}
                            metrics={{
                                ...(cheapestTicketData ?? {}),
                                ...(priceHistory?.metrics ?? {}),
                            }}
                        />
                    ))}
                    <TravelStats
                        bottomStat={parseInt(distanceInKm, 10)}
                        bottomStatTitle={t('distanceKilometers')}
                        description={t('distanceDescription')}
                        sectionTitle={t('distanceTitle')}
                        statType="distance"
                        topStat={parseInt(distanceInMiles, 10)}
                        topStatTitle={t('distanceMiles')}
                    />
                    <TravelStats
                        bottomStat={quietestDayOfWeek}
                        bottomStatTitle={t('leastBusyOn')}
                        description={t('busiestTimeDescription')}
                        sectionTitle={t('busiestTimeTitle')}
                        statType="busiestTimes"
                        topStat={busiestDayOfWeek}
                        topStatTitle={t('busiestOn')}
                    />
                    <TravelStats
                        bottomStat={latestDepartureHour[1]}
                        bottomStatTitle={t('latestDeparture')}
                        bottomStatUnits={latestDepartureHour[2]}
                        description={t('earliestLatestDescription', earliestLatestDescriptionData)}
                        sectionTitle={t('earliestLatestTitle')}
                        statType="departureTimes"
                        topStat={earliestDepartureHour[1]}
                        topStatTitle={t('earliestDeparture')}
                        topStatUnits={earliestDepartureHour[2]}
                    />
                    <TravelStats
                        bottomStat={avgDailyDirectTrips}
                        bottomStatTitle={t('directTrips')}
                        description={t('dailyTripsDescription', dailyTripsDescriptionData)}
                        sectionTitle={t('dailyTripsTitle')}
                        statType="dailyTrips"
                        topStat={avgDailyTrips}
                        topStatTitle={t('tripsPerDay')}
                    />
                </FAQSection>
                <SplitTest testName="c2cRevamp">
                    <Control />
                    <Variant name="group_a">
                        <GeneralSection
                            description={t('travelTipsDescription')}
                            id="travel-tips"
                            show={isTranslated(t, ['travelTipsTitle', 'travelTipsDescription'])}
                            title={t('travelTipsTitle')}
                        />
                        {(altTransModeStats?.cheapest || altTransModeStats?.fastest) && (
                            <AlternativeMode
                                data={altTransModeStats}
                                departDate={timeTableDate}
                                locationData={locationData}
                            />
                        )}
                    </Variant>
                </SplitTest>
                {!!longFormContent.sections && !!longFormContent.sections.length && (
                    <LongFormContent
                        intro={intro}
                        namespace={cityToCityConfig.namespace}
                        sections={sections}
                        shortcuts={shortcuts}
                        useSEOStyles
                    />
                )}
                <SplitTest testName="c2cRevamp">
                    <Control>
                        <EditorialBlurbs
                            arrivalDescription={cityContent.arriveContent.cityDescription}
                            arrivalSubtitle={t('aboutArriveCitySubtitle')}
                            blurbContent={blurbSectionContent}
                            departureDescription={cityContent.departContent.cityDescription}
                            departureSubtitle={t('aboutDepartCitySubtitle')}
                            label="city-editorial-blurb"
                            namespace={cityToCityConfig.namespace}
                            showArriveSection={showCityBlurbArriveSection}
                            showDepartSection={showCityBlurbDepartSection}
                            title={t('aboutCityTitle')}
                            variant="cities"
                        />
                        <EditorialBlurbs
                            arrivalDescription={blurbSectionContent.arriveMainStationContent}
                            arrivalImageSubtitle={arriveStations[0]?.name
                                ? t('popularStationsArrivalMainSubtitle', {
                                    featuredArrivalStation: arriveStations[0].name,
                                })
                                : ''}
                            arrivalStationsSubtitle={t('popularStationsArrivalOtherSubtitle')}
                            arrivalSubtitle={t('popularStationsArrivalTitle')}
                            blurbContent={{
                                ...blurbSectionContent,
                                moovitDescription,
                                moovitTitle,
                            }}
                            departureDescription={blurbSectionContent.departMainStationContent}
                            departureImageSubtitle={departStations[0]?.name
                                ? t('popularStationsDepartureMainSubtitle', {
                                    featuredDepartureStation: departStations[0].name,
                                })
                                : ''}
                            departureStationsSubtitle={t('popularStationsDepartureOtherSubtitle')}
                            departureSubtitle={t('popularStationsDepartureTitle')}
                            label="station-editorial-blurb"
                            namespace={cityToCityConfig.namespace}
                            showArriveSection={showStationBlurbArriveSection}
                            showDepartSection={showStationBlurbDepartSection}
                            title={t('popularStationsTitle')}
                            variant="stations"
                        />
                        {showBlogBlurbSection && (
                            <EditorialBlurbs
                                blurbContent={blurbSectionContent}
                                label="blog-section"
                                namespace={cityToCityConfig.namespace}
                                title={t('blogTitle')}
                                variant="blog"
                                showDepartSection
                            />
                        )}
                    </Control>
                    <Variant name="group_a" />
                </SplitTest>
                {!!content?.reverseLink && (
                    <ReturnTripCTA reverseLink={content.reverseLink} />
                )}
                {hasInterlinks && (
                    <LinkSection title={t('interlinkToC2cTitle')}>
                        <LinkTable
                            columns={2}
                            data={interlinkData.depart}
                            title={t('interlinkToC2cDepartSubtitle')}
                            native
                        />
                        <LinkTable
                            columns={2}
                            data={interlinkData.arrive}
                            title={t('interlinkToC2cArriveSubtitle')}
                            native
                        />
                        {!!interlinkData.popular?.length && (
                            <LinkTable
                                columns={2}
                                data={interlinkData.popular}
                                title={t('interlinkToC2cPopularSubtitle')}
                                native
                            />
                        )}
                    </LinkSection>
                )}
                <ValueStatement namespace={cityToCityConfig.namespace} />
                <Breadcrumbs data={breadcrumbData} />
                <BackToTopButton />
            </main>
        );
    }
}

const interlinkShape = PropTypes.shape({
    linkAdornment: PropTypes.string,
    linkAnchorText: PropTypes.string,
    linkUrl: PropTypes.string,
});

const imageShape = PropTypes.shape({
    metadata: PropTypes.shape({
        alt: PropTypes.string,
        author: PropTypes.string,
        license: PropTypes.string,
    }),
    name: PropTypes.string,
    size: PropTypes.number,
    src: PropTypes.string,
});

const stationShape = PropTypes.shape({
    code: PropTypes.string,
    images: PropTypes.arrayOf(imageShape),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    name: PropTypes.string,
});

const tripShape = PropTypes.shape({
    accessibility: PropTypes.shape({
        maxWheelchairSeats: PropTypes.number,
    }),
    arriveDateTimeUTC: PropTypes.number,
    arriveId: PropTypes.string,
    arriveName: PropTypes.string,
    carrierCode: PropTypes.string,
    created: PropTypes.string,
    departDateTImeUTC: PropTypes.number,
    departId: PropTypes.string,
    departName: PropTypes.string,
    duration: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string),
    isLuxury: PropTypes.bool,
    isRouted: PropTypes.bool,
    localizedArriveDateTime: PropTypes.string,
    localizedDepartDateTime: PropTypes.string,
    pricing: PropTypes.arrayOf(
        PropTypes.objectOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ),
    ),
    transfers: PropTypes.number,
    tripId: PropTypes.string,
    tripMode: PropTypes.arrayOf(PropTypes.string),
});

tripShape.tripLegs = PropTypes.shape({
    all: PropTypes.arrayOf(tripShape).isRequired,
    filtered: PropTypes.arrayOf(tripShape).isRequired,
});

CityToCity.propTypes = {
    altTransModeStats: PropTypes.shape({
        avg_bus_duration: PropTypes.number,
        avg_bus_price_usd: PropTypes.number,
        avg_daily_bus_trips: PropTypes.number,
        avg_daily_train_trips: PropTypes.number,
        avg_train_duration: PropTypes.number,
        avg_train_price_usd: PropTypes.number,
        cheapest: PropTypes.oneOf(['bus', 'train']),
        fastest: PropTypes.oneOf(['bus', 'train']),
    }),
    blurbSectionContent: PropTypes.shape({
        arriveCityImages: PropTypes.arrayOf(imageShape),
        arriveMainStationContent: PropTypes.string,
        arriveStationImages: PropTypes.arrayOf(imageShape),
        arriveStations: PropTypes.arrayOf(stationShape),
        blogPosts: PropTypes.arrayOf(PropTypes.shape({
            alt: PropTypes.string,
            author: PropTypes.string,
            src: PropTypes.string,
            title: PropTypes.string,
            url: PropTypes.string,
        })),
        departCityImages: PropTypes.arrayOf(imageShape),
        departMainStationContent: PropTypes.string,
        departStationImages: PropTypes.arrayOf(imageShape),
        departStations: PropTypes.arrayOf(stationShape),
    }),
    breadcrumbData: PropTypes.shape({
        activeIndex: PropTypes.shape({ name: PropTypes.string }),
        toCity: PropTypes.shape({
            name: PropTypes.string,
            url: PropTypes.string,
        }),
        toCountry: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
            url: PropTypes.string,
        }),
        toState: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
            url: PropTypes.string,
        }),
        transMode: PropTypes.shape({
            name: PropTypes.string,
            url: PropTypes.string,
        }),
    }),
    c2cRevampVariant: PropTypes.string,
    carrierLinks: PropTypes.arrayOf(PropTypes.shape({
        averageDailyTrips: PropTypes.number,
        carrierCode: PropTypes.string,
        carrierName: PropTypes.string,
        minPrice: PropTypes.number,
        url: PropTypes.string,
    })),
    cheapestTicketData: PropTypes.shape({
        cheapestAvgPrice: PropTypes.number,
        cheapestPrice: PropTypes.number,
        savingsPriceUsd: PropTypes.number,
    }).isRequired,
    cityContent: PropTypes.shape({
        arriveContent: PropTypes.shape({
            cityDescription: PropTypes.string,
        }),
        departContent: PropTypes.shape({
            cityDescription: PropTypes.string,
        }),
    }),
    cityToCityCarrierData: PropTypes.arrayOf(PropTypes.shape({
        carrierCode: PropTypes.string,
        carrierGuide: PropTypes.string,
        carrierName: PropTypes.string,
        content: PropTypes.shape({
            carrierCardLabel: PropTypes.string,
            carrierCardTextAMT: PropTypes.string,
            shortAvgPrice: PropTypes.string,
            shortAvgTime: PropTypes.string,
        }),
        images: PropTypes.arrayOf(imageShape),
        metrics: PropTypes.shape({
            averageDailyTrips: PropTypes.number,
            averageDurationMinutes: PropTypes.number,
            averagePriceUsd: PropTypes.number,
            minPriceUsd: PropTypes.number,
            weekendPriceUsd: PropTypes.number,
        }),
        stations: PropTypes.shape({
            topArrive: PropTypes.string,
            topDepart: PropTypes.string,
        }),
    })).isRequired,
    content: PropTypes.objectOf(PropTypes.string).isRequired,
    dailyTripsData: PropTypes.shape({
        avgDailyDirectTrips: PropTypes.number,
        avgDailyTrips: PropTypes.number,
        carriers: PropTypes.number,
        earliestDeparture: PropTypes.string,
        latestDeparture: PropTypes.string,
        minDuration: PropTypes.number,
        otherTransModePricing: PropTypes.shape({
            price1Usd: PropTypes.number,
            price2Usd: PropTypes.number,
            priceReverseUsd: PropTypes.number,
        }),
    }).isRequired,
    distanceData: PropTypes.shape({
        km: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        mi: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    i18nMoment: PropTypes.func.isRequired,
    i18nMoney: PropTypes.func.isRequired,
    infoSectionContent: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        prefix: PropTypes.string,
        text: PropTypes.string,
        title: PropTypes.string,
    })),
    interlinkData: PropTypes.objectOf(PropTypes.arrayOf(interlinkShape)),
    isFetchingAllTrips: PropTypes.bool,
    isFetchingCachedTrips: PropTypes.bool,
    journeyInfo: PropTypes.shape({
        cheapestPrice: PropTypes.number,
        directTripsPerDay: PropTypes.number,
        minDurationMinutes: PropTypes.number,
        mostOptionsCarrierName: PropTypes.string,
        numberOfCarriers: PropTypes.number,
    }),
    language: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    locationData: PropTypes.objectOf(PropTypes.shape({
        content: PropTypes.objectOf(PropTypes.string),
        countryCode: PropTypes.string,
        countryName: PropTypes.string,
        formattedAddress: PropTypes.string,
        images: PropTypes.arrayOf(imageShape),
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        name: PropTypes.string,
        placeId: PropTypes.string,
        state: PropTypes.string,
        stateCode: PropTypes.string,
        stateName: PropTypes.string,
        timezone: PropTypes.string,
        wcityid: PropTypes.string,
    })).isRequired,
    longFormContent: PropTypes.shape({
        intro: PropTypes.string,
        sections: PropTypes.arrayOf(PropTypes.shape({
            description: PropTypes.string,
            id: PropTypes.string,
            title: PropTypes.string,
        })),
        shortcuts: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            text: PropTypes.string,
        })),
    }),
    onTimeTableDateSelect: PropTypes.func,
    priceHistory: PropTypes.shape({
        daily: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.string,
            y: PropTypes.number,
        })),
        daysUntilDepart: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
        })),
        metrics: PropTypes.shape({
            minUsdLonghaul: PropTypes.number,
            minUsdMediumhaul: PropTypes.number,
            minUsdOutofstate: PropTypes.number,
            minUsdShorthaul: PropTypes.number,
        }),
        monthly: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
        })),
        xTickValuesDaily: PropTypes.arrayOf(PropTypes.string),
        xTickValuesDepartDays: PropTypes.arrayOf(PropTypes.number),
        xTickValuesMonthly: PropTypes.arrayOf(PropTypes.number),
    }),
    renderFlags: PropTypes.shape({
        showBlogBlurbSection: PropTypes.bool,
        showCityBlurbArriveSection: PropTypes.bool,
        showCityBlurbDepartSection: PropTypes.bool,
        showStationBlurbArriveSection: PropTypes.bool,
        showStationBlurbDepartSection: PropTypes.bool,
    }),
    reviewSummary: PropTypes.shape({
        overall: PropTypes.number,
        reviewCount: PropTypes.number,
        travelType: PropTypes.number,
    }),
    routeMapImageSlug: PropTypes.string,
    scheduleData: PropTypes.array,
    simplifiedCarrierReviews: PropTypes.arrayOf(PropTypes.shape({
        content: PropTypes.shape({
            individualReviewCardOverall: PropTypes.string,
            individulaReviewCardTravelerType: PropTypes.string,
        }),
        stats: PropTypes.shape({
            reviewCarrierGuideSlug: PropTypes.string,
            reviewCarrierName: PropTypes.string,
            reviewIndividualCons: PropTypes.string,
            reviewIndividualDepartDate: PropTypes.string,
            reviewIndividualFirstName: PropTypes.string,
            reviewIndividualId: PropTypes.number,
            reviewIndividualPros: PropTypes.string,
            reviewIndividualTips: PropTypes.string,
            reviewIndividualTravelerType: PropTypes.number,
        }),
    })),
    t: PropTypes.func.isRequired,
    timeTableDate: PropTypes.string,
    transMode: PropTypes.string.isRequired,
    travelFrequencyData: PropTypes.shape({
        busiestDayOfWeek: PropTypes.string,
        quietestDayOfWeek: PropTypes.string,
    }).isRequired,
    upcomingTrips: PropTypes.arrayOf(tripShape),
};

CityToCity.defaultProps = {
    altTransModeStats: {},
    blurbSectionContent: {},
    breadcrumbData: {},
    carrierLinks: [],
    cityContent: {},
    infoSectionContent: [],
    interlinkData: {},
    isFetchingAllTrips: false,
    isFetchingCachedTrips: false,
    journeyInfo: {},
    longFormContent: {},
    onTimeTableDateSelect: () => { return undefined; },
    priceHistory: {},
    renderFlags: {
        showBlogBlurbSection: false,
        showCityBlurbArriveSection: false,
        showCityBlurbDepartSection: false,
        showStationBlurbArriveSection: false,
        showStationBlurbDepartSection: false,
    },
    reviewSummary: {},
    routeMapImageSlug: '',
    scheduleData: [],
    simplifiedCarrierReviews: [],
    timeTableDate: '',
    upcomingTrips: [],

};

export {
    CityToCity,
    splitShortTimeString,
};
export default localize(CityToCity, cityToCityConfig.namespace);
